import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from './baseQuery';
import { IPagedResult } from '../../shared/models/paged-result';
import { IPointOfSale } from '../../shared/models/core/point-of-sale';
import { IMarket } from '../../shared/models/core/market';
import { serialize } from '../../utility/serialize';
import { PointOfSaleFilterSet } from '../../shared/models/core/point-of-sale-filter-set';

const pointOfSaleApi = createApi({
  reducerPath: 'pointOfSaleApi',
  baseQuery,
  tagTypes: ['PointOfSales', 'Markets'],
  endpoints: (builder) => ({
    getPointsOfSale: builder.query<
      IPagedResult<IPointOfSale>,
      PointOfSaleFilterSet | string | void
    >({
      query: (filterSet) => {
        const qs = filterSet ? `?${serialize(filterSet)}` : '';
        return {
          url: `/api/pointofsales${qs}`,
          method: 'GET',
        };
      },
      providesTags: ['PointOfSales'],
    }),
    getPointOfSale: builder.query<IPointOfSale, string>({
      query: (id) => ({
        url: `/api/pointofsales/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: 'PointOfSales', id }],
    }),
    listMarkets: builder.query<IPagedResult<IMarket>, void>({
      query: () => ({
        url: '/api/markets',
        method: 'GET',
        params: {
          size: 1000,
        },
      }),
      providesTags: ['Markets'],
    }),
    createPointOfSale: builder.mutation<IPointOfSale, IPointOfSale>({
      query: (body) => ({
        url: '/api/pointofsales',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PointOfSales'],
    }),
    updatePointOfSale: builder.mutation<IPointOfSale, IPointOfSale>({
      query: (body) => ({
        url: `/api/pointofsales/${body.id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['PointOfSales'],
    }),
    deletePointOfSale: builder.mutation<void, string>({
      query: (id) => ({
        url: `/api/pointofsales/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['PointOfSales'],
    }),
  }),
});

export { pointOfSaleApi };

export const {
  useGetPointsOfSaleQuery,
  useLazyGetPointsOfSaleQuery,
  useGetPointOfSaleQuery,
  useListMarketsQuery,
  useCreatePointOfSaleMutation,
  useUpdatePointOfSaleMutation,
  useDeletePointOfSaleMutation,
} = pointOfSaleApi;
